import { useEffect, type RefObject } from "react"
import { useVirtualizer, type Virtualizer } from "@tanstack/react-virtual"
import { Table } from "@tanstack/react-table"
import { ROW_HEIGHT, OVERSCAN_COUNT, type ExtendedGuestSummary } from "./helpers"

type UseTableVirtualizationArgs = {
  scrollContainerRef: RefObject<HTMLDivElement>
  table: Table<ExtendedGuestSummary>
  scrollMargin?: number
  onScrollEnd?: () => void
}

const useTableVirtualization = ({
  scrollContainerRef,
  table,
  scrollMargin = 0,
  onScrollEnd,
}: UseTableVirtualizationArgs): Virtualizer<HTMLDivElement, Element> => {
  const rowVirtualizer = useVirtualizer<HTMLDivElement, Element>({
    count: table.getRowModel().rows.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: OVERSCAN_COUNT,
    scrollMargin,
  })

  const virtualItems = rowVirtualizer.getVirtualItems()
  const lastVirtualItemIndex = virtualItems[virtualItems.length - 1]?.index ?? -1
  const rowCount = table.getRowModel().rows.length

  useEffect(() => {
    if (!onScrollEnd) return
    if (lastVirtualItemIndex < 0) return
    if (lastVirtualItemIndex >= rowCount - 1) {
      onScrollEnd()
    }
  }, [lastVirtualItemIndex, rowCount, onScrollEnd])

  return rowVirtualizer
}

export default useTableVirtualization
